/**
 * Diktierten Text dort einsetzen, wo der Cursor beim Start der Aufnahme stand —
 * so, als wäre er getippt worden (gleiche Speicher-/Input-Wege wie beim Tippen).
 */

/** Ein Leerzeichen davor, wenn der Text sonst an ein Wort kleben würde. */
export function mitAbstand(davor: string, text: string): string {
    if (!text) return text;
    const letztes = davor.slice(-1);
    return letztes && !/\s/.test(letztes) && !/^[\s.,;:!?)]/.test(text) ? ` ${text}` : text;
}

/** Textarea/Input: Text zwischen `start` und `ende` ersetzen. Rein, ohne DOM. */
export function textareaEinsetzen(
    wert: string,
    start: number,
    ende: number,
    text: string,
): { wert: string; caret: number } {
    const a = Math.max(0, Math.min(start, wert.length));
    const b = Math.max(a, Math.min(ende, wert.length));
    const einsatz = mitAbstand(wert.slice(0, a), text);
    return { wert: wert.slice(0, a) + einsatz + wert.slice(b), caret: a + einsatz.length };
}

/** Den Text vor einer Range innerhalb ihres Blocks (für {@link mitAbstand}). */
function textVor(range: Range): string {
    const r = range.cloneRange();
    r.collapse(true);
    const c = r.startContainer;
    if (c.nodeType === Node.TEXT_NODE) return (c.textContent ?? "").slice(0, r.startOffset);
    const vorher = c.childNodes[r.startOffset - 1];
    return vorher?.textContent ?? "";
}

/**
 * Fenster und Dokument DES FELDES — nicht `window`/`document`: ein ausgelagertes
 * KMVS-Fenster (eigenes Browserfenster) trägt das Feld in einem anderen Dokument.
 */
const dokVon = (el: HTMLElement): Document => el.ownerDocument ?? document;
const auswahlVon = (el: HTMLElement): Selection | null =>
    (dokVon(el).defaultView ?? window).getSelection();

/** Die aktuelle Auswahl, wenn sie in `wurzel` liegt — als Kopie zum Merken. */
export function auswahlIn(wurzel: HTMLElement | null): Range | null {
    if (!wurzel) return null;
    const sel = auswahlVon(wurzel);
    if (!sel || !sel.rangeCount) return null;
    const r = sel.getRangeAt(0);
    return wurzel.contains(r.commonAncestorContainer) ? r.cloneRange() : null;
}

/**
 * contentEditable: Auswahl wiederherstellen (oder `ersatz()` nehmen, wenn die
 * gemerkte Stelle nicht mehr im Feld steht) und den Text per
 * `execCommand("insertText")` einsetzen. Das löst `beforeinput`/`input` aus wie
 * Tippen, also speichern Felder, die auf `input` hören, ganz normal; und
 * Strg+Z nimmt das Diktat in einem Schritt zurück.
 */
export function inEditableEinfuegen(
    wurzel: HTMLElement,
    gemerkt: Range | null,
    text: string,
    ersatz?: () => Range | null,
): boolean {
    const sel = auswahlVon(wurzel);
    if (!sel) return false;
    const dok = dokVon(wurzel);
    let r = gemerkt && gemerkt.startContainer.isConnected && wurzel.contains(gemerkt.commonAncestorContainer) ? gemerkt : null;
    if (!r) r = ersatz?.() ?? null;
    if (!r) {
        r = dok.createRange();
        r.selectNodeContents(wurzel);
        r.collapse(false);
    }
    wurzel.focus({ preventScroll: true });
    sel.removeAllRanges();
    sel.addRange(r);
    const einsatz = r.collapsed ? mitAbstand(textVor(r), text) : text;
    // execCommand ist offiziell „veraltet“, aber der einzige Weg, der die
    // Rückgängig-Historie des Browsers füttert; alle Browser unterstützen ihn.
    return dok.execCommand("insertText", false, einsatz);
}
